/**
 * AI Step Executors — AI analysis, summarization, and extraction as workflow steps
 *
 * Config:
 * - prompt: string — The instruction/query to process
 * - input: string — Input text to analyze (supports {{variable}})
 * - model?: string — Optional model override
 */

import type { EngineStep, EngineExecutionContext, StepExecutionResult, StepExecutor } from '../types';
import { preprocessQuery } from '../../runner';

export const aiStepExecutors: StepExecutor[] = [
  {
    type: 'ai_analyze',
    async execute(step: EngineStep): Promise<StepExecutionResult> {
      try {
        const prompt = step.config.prompt as string || 'Analyze the following data';
        const input = step.config.input as string || '';
        const fullQuery = `${prompt}\n\nData:\n${input}`;

        const result = await preprocessQuery(fullQuery);

        return {
          success: true,
          output: {
            aiAnalysis: result,
            intent: result.intent,
            fetchedData: result.fetchedData,
          },
        };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : 'AI analysis failed',
        };
      }
    },
  },
  {
    type: 'ai_summarize',
    async execute(step: EngineStep): Promise<StepExecutionResult> {
      try {
        const input = step.config.input as string || '';
        const language = step.config.language as string || 'Italian';
        const prompt = `Summarize the following in ${language}, keeping key points concise:\n\n${input}`;

        const result = await preprocessQuery(prompt);

        return {
          success: true,
          output: {
            summary: result,
            intent: result.intent,
          },
        };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : 'AI summarization failed',
        };
      }
    },
  },
  {
    type: 'ai_extract',
    async execute(step: EngineStep): Promise<StepExecutionResult> {
      try {
        const input = step.config.input as string || '';
        const fields = step.config.fields as string[] || [];
        const prompt = fields.length > 0
          ? `Extract the following fields from the text: ${fields.join(', ')}.\n\nText:\n${input}`
          : `Extract key information from:\n\n${input}`;

        const result = await preprocessQuery(prompt);

        return {
          success: true,
          output: {
            extraction: result,
            intent: result.intent,
          },
        };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : 'AI extraction failed',
        };
      }
    },
  },
];
